import { QueryClient, QueryKey } from '@tanstack/react-query';
import type { RawApi, Mapper } from '../types/api-brand';

/**
 * Type-safe replacement for queryClient.setQueryData.
 *
 * Forces the caller to pass either:
 *   - A pre-mapped app entity (TApp), or
 *   - A raw API response (RawApi<TRaw>) plus a mapper function
 *
 * This prevents the class of bug where raw snake_case API data gets cached
 * and crashes components that read mapped fields. This caused a production
 * blue-screen bug in CustomerEmailPromptModal (May 2026).
 *
 * Migration: replace queryClient.setQueryData(key, data) with
 *   setQueryDataMapped(queryClient, key, rawData, mapperFn)
 * or
 *   setQueryDataMapped(queryClient, key, mappedData)
 */
export function setQueryDataMapped<TApp>(
  client: QueryClient,
  key: QueryKey,
  data: TApp
): void;
export function setQueryDataMapped<TRaw, TApp>(
  client: QueryClient,
  key: QueryKey,
  raw: RawApi<TRaw>,
  mapper: Mapper<TRaw, TApp>
): void;
export function setQueryDataMapped<TRaw, TApp>(
  client: QueryClient,
  key: QueryKey,
  data: TApp | RawApi<TRaw>,
  mapper?: Mapper<TRaw, TApp>
): void {
  if (mapper) {
    // eslint-disable-next-line no-restricted-syntax -- this IS the safe wrapper
    client.setQueryData(key, mapper(data as RawApi<TRaw>));
  } else {
    // eslint-disable-next-line no-restricted-syntax -- this IS the safe wrapper
    client.setQueryData(key, data);
  }
}

/**
 * For optimistic updates with functional setters. The setter receives the
 * existing cached value (TApp shape, already mapped) and returns a new value
 * of the same shape. This is safe because no API data crosses the boundary.
 */
export function updateQueryDataMapped<TApp>(
  client: QueryClient,
  key: QueryKey,
  updater: (prev: TApp | undefined) => TApp | undefined
): void {
  // eslint-disable-next-line no-restricted-syntax -- this IS the safe wrapper
  client.setQueryData<TApp>(key, updater);
}
